import { useEffect, useState } from "react";
import { toast } from "sonner";
import { User, Mail, Phone, Building, MapPin, Shield, Save, Loader2, FileText, Upload, Download, Trash2, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import DocumentViewer from "./DocumentViewer";

export default function MyProfile() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", company: "", address: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", company: user.company || "", address: user.address || "" });
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/profile", form);
      updateUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const roleLabel = { admin: "Administrator", power_user: "Power User", client: "Client", legal_rep: "Legal Rep", accountant: "Accountant", broker: "Broker", caretaker: "Caretaker" }[user?.role] || "Client";

  return (
    <div className="max-w-3xl">
      <p className="text-xs uppercase tracking-[0.2em] font-mono text-emerald-700 font-semibold">Account</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight font-display text-slate-900">My Profile</h1>

      <div className="mt-8 flex items-center gap-5 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
        <div className="h-16 w-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-2xl font-semibold text-emerald-700">
          {(user?.name || "?").charAt(0)}
        </div>
        <div>
          <div className="text-xl font-semibold text-slate-900">{user?.name}</div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
            <Mail className="h-4 w-4" /> {user?.email}
          </div>
          <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-mono px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
            <Shield className="h-3 w-3" /> {roleLabel}
          </span>
        </div>
      </div>

      <form onSubmit={save} className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-5">
        <h2 className="text-lg font-semibold text-slate-900">Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field icon={User} label="Full name" testid="profile-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field icon={Phone} label="Phone" testid="profile-phone-input" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Field icon={Building} label="Company" testid="profile-company-input" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
          <Field icon={MapPin} label="Address" testid="profile-address-input" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
        </div>
        <button type="submit" disabled={busy} data-testid="profile-save-button"
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-sm disabled:opacity-60 active:scale-[0.98]">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </button>
      </form>

      <IdentitySection role={user?.role} />
    </div>
  );
}

const DOC_TYPES = ["Passport", "Driving License", "Aadhar Card", "PAN Card"];

function IdentitySection({ role }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState("");
  const [expiry, setExpiry] = useState("");
  const [viewDoc, setViewDoc] = useState(null);
  const [uploading, setUploading] = useState(false);

  const canUpload = ["client", "admin", "power_user", "legal_rep", "accountant", "broker", "caretaker"].includes(role);
  const canDelete = ["admin", "power_user"].includes(role);
  const canDownload = ["admin", "power_user"].includes(role);
  const visible = ["client", "admin", "power_user", "legal_rep", "accountant", "broker", "caretaker"].includes(role);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/profile/identity"); setDocs(data); }
    catch (e) { /* not permitted */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (visible) load(); }, [visible]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !docType) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/profile/identity", { doc_type: docType, file: reader.result, file_name: file.name, expiry_date: expiry });
        setDocs((d) => [...d, data]);
        setDocType(""); setExpiry("");
        toast.success(`${data.doc_type} uploaded`);
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
      finally { setUploading(false); }
    };
    reader.onerror = () => { setUploading(false); toast.error("Could not read file"); };
    reader.readAsDataURL(file);
  };

  const remove = async (id) => {
    try { await api.delete(`/profile/identity/${id}`); setDocs((d) => d.filter((x) => x.id !== id)); toast.success("Document deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  };

  const download = (doc) => {
    const a = document.createElement("a");
    a.href = doc.file; a.download = doc.file_name || doc.doc_type; a.click();
  };

  if (!visible) return null;

  return (
    <div className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid="identity-section">
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-emerald-600" />
        <h2 className="text-lg font-semibold text-slate-900">Identity</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">Government-issued identity documents.</p>

      {canUpload && (
        <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="text-sm text-slate-600">Document type</label>
            <select data-testid="identity-doctype-select" value={docType} onChange={(e) => setDocType(e.target.value)}
              className="mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
              <option value="">Select a document…</option>
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="sm:w-44">
            <label className="text-sm text-slate-600">Expiry date</label>
            <input type="date" data-testid="identity-expiry-input" value={expiry} onChange={(e) => setExpiry(e.target.value)}
              className="mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
          </div>
          <label data-testid="identity-upload-button"
            className={`inline-flex items-center justify-center gap-2 font-semibold px-5 py-2.5 rounded-lg transition-all shadow-sm ${docType && !uploading ? "bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer active:scale-[0.98]" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Document
            <input type="file" accept="image/*,application/pdf" disabled={!docType || uploading} onChange={onFile} className="hidden" data-testid="identity-file-input" />
          </label>
        </div>
      )}

      <div className="mt-5 space-y-2" data-testid="identity-doc-list">
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
        ) : docs.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">No documents uploaded yet.</p>
        ) : docs.map((doc) => (
          <div key={doc.id} data-testid="identity-doc-item" className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
            <FileText className="h-5 w-5 text-slate-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 truncate">{doc.doc_type}</div>
              <div className="text-xs text-slate-400 font-mono truncate">{doc.file_name}</div>
              {doc.expiry_date && (
                <div className="text-xs mt-0.5 font-mono flex items-center gap-1.5">
                  <span className={new Date(doc.expiry_date) < new Date() ? "text-rose-600" : "text-slate-500"}>Expires {doc.expiry_date}</span>
                  {new Date(doc.expiry_date) < new Date() && <span className="px-1.5 py-0.5 rounded-full bg-rose-100 border border-rose-200 text-rose-700">Expired</span>}
                </div>
              )}
            </div>
            <button onClick={() => setViewDoc(doc)} data-testid="identity-view-button"
              className="p-2 rounded-lg text-slate-500 hover:bg-white hover:text-emerald-700 transition-colors" title="View"><Eye className="h-4 w-4" /></button>
            {canDownload && (
              <button onClick={() => download(doc)} data-testid="identity-download-button"
                className="p-2 rounded-lg text-slate-500 hover:bg-white hover:text-emerald-700 transition-colors" title="Download"><Download className="h-4 w-4" /></button>
            )}
            {canDelete && (
              <button onClick={() => remove(doc.id)} data-testid="identity-delete-button"
                className="p-2 rounded-lg text-rose-500 hover:bg-white transition-colors" title="Delete"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>
      {viewDoc && <DocumentViewer doc={viewDoc} canDownload={canDownload} onClose={() => setViewDoc(null)} />}
    </div>
  );
}

const Field = ({ icon: Icon, label, value, onChange, testid }) => (
  <div>
    <label className="text-sm text-slate-600">{label}</label>
    <div className="mt-1.5 flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-3 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/20">
      <Icon className="h-4 w-4 text-slate-400" />
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent py-3 text-slate-900 focus:outline-none" />
    </div>
  </div>
);
